import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Badge,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import HistoryIcon from "@mui/icons-material/History";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { isValid, startOfDay } from "date-fns";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import {
  createA3Pedidos,
  deleteA3Pedidos,
  getAllPresupuestoA3Pedidos,
  updateA3Pedidos,
} from "../../api/Produccion/a3ArticulosApi";
import {
  A3_EMPRESAS_PEDIDO,
  getEmpresaA3Meta,
  normalizeEmpresaA3,
} from "../../constants/a3Empresas";
import {
  getPresupuestosByProyectoId,
  backfillCriticalPathFromPedidos,
  linkCriticalPathFromPedido,
} from "../../api/Estimates/estimatesApi";
import { getLogisticaEnviosResumen } from "../../api/Logistica/logisticaEnviosApi";
import { getUserById } from "../../api/User/userApi";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import ProduccionPedidosDialog from "./ProduccionPedidosDialog";
import PedidoEnviosDialog from "./PedidoEnviosDialog";
import PedidoCompraPrint from "./PedidoCompraPrint";
import { normalizeFechaIso } from "./a3PedidoMatrizUtils";
import {
  A3_COL_COMPRA_WIDTH,
  A3_COL_LINK_SUMMARY_WIDTH,
  A3_COL_VENTA_WIDTH,
  a3PedidosCellClipSx,
  a3PedidosTableContainerSx,
  a3PedidosTableFixedSx,
  a3TableDenseSx,
} from "../../constants/a3PedidosTableLayout";
import { computePrendasUnidadesPedido } from "../../utils/cpUnits";
import {
  a3PedidoRowReactKey,
  buildOptimisticA3PedidoRow,
  formatA3PartyLabel,
  nextReferenciaPedidoSuffix,
} from "../../utils/a3PedidosListUtils";
import { dateFnsLocaleFromI18n } from "../../utils/i18nLocale";
import {
  agruparPorArticulo,
  calcResumenMargenGrupo,
  NO_ARTICLE_CODE,
  fmtA3Cantidad,
  fmtA3Estado,
  fmtA3Importe,
  getPoCompraA3,
  getPoVentaA3,
  getProduccionEmpresaTreeAccent,
  getProduccionGrupoDePedido,
  getProduccionPedidoTreeDate,
  getProduccionPedidoTreeId,
  getProduccionPedidoTreeLabel,
  groupProduccionPedidosByCliente,
  isA3Served,
  resolveUnitPriceForDisplay,
  startOfToday,
  toYmd,
  totalesPedidosGrupo,
} from "./produccionSummaryUtils";
import ClientesNavTree from "../shared/ClientesNavTree";
import {
  pageContentCardSx,
  pageFieldSx,
  pageHeroShellSx,
  pageKpiChipSx,
  pageSectionTitleSx,
} from "../../styles/pageModernStyles";

const grupoAccordionSummarySx = {
  px: { xs: 1.25, sm: 1.75 },
  py: { xs: 1.15, sm: 1.25 },
  alignItems: "center",
  "& .MuiAccordionSummary-content": {
    margin: "8px 0",
    minWidth: 0,
    flexGrow: 1,
    overflow: "visible",
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    alignSelf: "center",
    flexShrink: 0,
  },
};

function GrupoResumenChip({ label, value, color }) {
  return (
    <Chip
      size="medium"
      label={`${label} ${value}`}
      sx={{
        height: 36,
        flexShrink: 0,
        borderRadius: 2,
        fontWeight: 800,
        fontSize: "0.8rem",
        bgcolor: alpha(color, 0.12),
        color,
        border: "1px solid",
        borderColor: alpha(color, 0.34),
        "& .MuiChip-label": {
          px: 1.2,
          py: 0.55,
        },
      }}
    />
  );
}

const fmtA3PorcIva = (v) => {
  if (v === undefined || v === null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n)
    ? `${n.toLocaleString("es-ES", { maximumFractionDigits: 2 })} %`
    : "—";
};

const fmtA3MargenPct = (v) => {
  if (!Number.isFinite(v)) return "—";
  return `${v.toLocaleString("es-ES", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  })} %`;
};

const a3EstadoBadgeSx = (estado, detalle) => {
  const s = String(estado || "").trim();
  const d = String(detalle || "").trim();
  if (!s && !d) return { bgcolor: "grey.200", color: "text.secondary" };
  if (isA3Served(estado, detalle))
    return { bgcolor: "success.main", color: "common.white" };
  return { bgcolor: "warning.main", color: "common.white" };
};

function formatFecha(raw) {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleString("es-ES", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function etiquetaMonedaResumen(code) {
  const c = String(code || "")
    .trim()
    .toUpperCase();
  if (!c) return "";
  if (c === "EURO" || c === "EUR") return "€";
  if (c === "USD") return "$";
  return c;
}

const normA3 = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase();

const normTipoContable = (v) => {
  const t = String(v ?? "").trim();
  return t || "1";
};

const getRowTipoContable = (row) =>
  normTipoContable(row?.TipoContableVenta ?? row?.TipoContableCompra ?? "1");

const getClienteA3 = (row) =>
  formatA3PartyLabel(row?.CodCli, row?.ClienteNombre);
const getPedidoEnviosCount = (row, enviosCountByPedido = {}) =>
  Number(enviosCountByPedido?.[Number(row?.IdPresupuestoA3Pedido)] || 0) || 0;
const rowTieneVentaA3 = (row) => Number(row?.IdPedv) > 0;
const truncA3 = (value, max = 35) => {
  const txt = String(value || "").trim();
  if (!txt) return "—";
  return txt.length > max ? `${txt.slice(0, max)}...` : txt;
};

function TablaPedidosA3Grupo({
  pedidos,
  onEditRow,
  onDeleteRow,
  onEnviosRow,
  puedeEditarPedidosA3,
  enviosCountByPedido = {},
  selectedPedidoId = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <TableContainer
      sx={{
        ...a3PedidosTableContainerSx,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: alpha(theme.palette.background.paper, 0.92),
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          ...a3TableDenseSx,
          ...a3PedidosTableFixedSx,
          "& .MuiTableCell-head": {
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
            borderBottom: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <TableHead sx={{ display: { xs: "none", md: "table-header-group" } }}>
          <TableRow>
            <TableCell
              sx={{
                width: A3_COL_LINK_SUMMARY_WIDTH,
                verticalAlign: "middle",
                ...a3PedidosCellClipSx,
              }}
            >
              <Stack spacing={0.25}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  {t("produccionSummary.tableColPresupLink")}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell
              sx={{
                width: A3_COL_COMPRA_WIDTH,
                borderLeft: "2px solid",
                borderColor: "error.light",
                verticalAlign: "top",
                ...a3PedidosCellClipSx,
              }}
            >
              <Stack spacing={0.35} alignItems="stretch">
                <Typography
                  variant="caption"
                  sx={{
                    color: "error.main",
                    fontWeight: 800,
                    fontSize: "0.8rem",
                    letterSpacing: 0.3,
                  }}
                >
                  {t("produccionSummary.tableColPurchase")}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell
              sx={{
                width: A3_COL_VENTA_WIDTH,
                borderLeft: "2px solid",
                borderColor: "primary.main",
                verticalAlign: "top",
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                ...a3PedidosCellClipSx,
              }}
            >
              <Stack spacing={0.35} alignItems="stretch">
                <Typography
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    fontWeight: 800,
                    fontSize: "0.8rem",
                    letterSpacing: 0.3,
                  }}
                >
                  {t("produccionSummary.tableColSale")}
                </Typography>
              </Stack>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pedidos.map((row, rowIdx) => {
            const key = a3PedidoRowReactKey(row, rowIdx);
            const fechaCompraStr = formatFecha(
              row.FechaCompraA3 || row.FechaA3 || row.CreatedAt,
            );
            const fechaVentaStr = formatFecha(
              row.FechaVentaA3 || row.FechaA3 || row.CreatedAt,
            );
            const refStr = String(row.Referencia || "").trim() || "—";
            const presu =
              String(row.NombrePresupuesto || "").trim() ||
              (row.IdPresupuesto != null ? `#${row.IdPresupuesto}` : "—");
            const empresaA3 = normalizeEmpresaA3(row.EmpresaA3);
            const empresaMeta = getEmpresaA3Meta(empresaA3);
            const empresaBadge = empresaMeta.badge;

            const tieneCompra = row?.IdPedc != null && Number(row.IdPedc) > 0;
            const tieneVenta = row?.IdPedv != null && Number(row.IdPedv) > 0;
            const proveedorR = formatA3PartyLabel(
              row.CodPro,
              row.ProveedorNombre,
            );
            const clienteR = formatA3PartyLabel(row.CodCli, row.ClienteNombre);
            const pcom =
              row.SerieCompra != null && row.NumDocCompra != null
                ? `${String(row.TipoContableCompra || "1").trim()}/${String(row.SerieCompra).trim()}-${row.NumDocCompra}`
                : "";
            const pvta =
              row.SerieVenta != null && row.NumDocVenta != null
                ? `${String(row.TipoContableVenta || "1").trim()}/${String(row.SerieVenta).trim()}-${row.NumDocVenta}`
                : "";
            const estadoCompra = fmtA3Estado(
              row.EstadoCompra,
              row.EstadoDetalleCompra,
            );
            const estadoVenta = fmtA3Estado(
              row.EstadoVenta,
              row.EstadoDetalleVenta,
            );
            const monedaCompra = etiquetaMonedaResumen(
              row.MonedaCompraA3 || row.MonedaCoste || "",
            );
            const monedaVenta = etiquetaMonedaResumen(
              row.MonedaVentaA3 || row.MonedaVenta || "",
            );
            const precioCompraVista = resolveUnitPriceForDisplay(
              row.UnidadesCompra,
              row.PrecioCosteUn,
              row.TotalCompra,
              row.TotalDocumentoCompra,
            );
            const precioVentaVista = resolveUnitPriceForDisplay(
              row.UnidadesVenta,
              row.PrecioVentaUn,
              row.TotalVenta,
              row.TotalDocumentoVenta,
            );
            const bloqueadoBorrado =
              isA3Served(row?.EstadoCompra, row?.EstadoDetalleCompra) ||
              isA3Served(row?.EstadoVenta, row?.EstadoDetalleVenta);
            const enviosPedidoCount = getPedidoEnviosCount(
              row,
              enviosCountByPedido,
            );
            const bloqueadoPorLogistica = enviosPedidoCount > 0;
            const bloqueoDelete = bloqueadoBorrado || bloqueadoPorLogistica;
            const isSelectedRow =
              Number(row?.IdPresupuestoA3Pedido) === Number(selectedPedidoId);

            return (
              <TableRow
                key={key}
                hover
                sx={{
                  display: { xs: "block", md: "table-row" },
                  border: { xs: "1px solid", md: "none" },
                  borderColor: { xs: "divider", md: "transparent" },
                  borderRadius: { xs: 1, md: 0 },
                  mb: { xs: 1, md: 0 },
                  transition: "background-color .15s ease",
                  backgroundColor: isSelectedRow
                    ? alpha(theme.palette.primary.main, 0.06)
                    : "transparent",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.035),
                  },
                }}
              >
                <TableCell
                  sx={{
                    width: { xs: "100%", md: A3_COL_LINK_SUMMARY_WIDTH },
                    display: { xs: "block", md: "table-cell" },
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.5,
                    ),
                    ...a3PedidosCellClipSx,
                  }}
                >
                  <Stack
                    spacing={1}
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="stretch"
                    sx={{
                      p: 1,
                      border: "1px solid",
                      borderColor: isSelectedRow
                        ? alpha(theme.palette.primary.main, 0.35)
                        : "divider",
                      borderRadius: 1.5,
                      backgroundColor: alpha(
                        theme.palette.background.paper,
                        0.86,
                      ),
                    }}
                  >
                    {puedeEditarPedidosA3 ? (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        useFlexGap
                        flexWrap="wrap"
                        alignItems="center"
                      >
                          <Tooltip
                            title={t("produccionSummary.companyTooltip", {
                              name: empresaMeta.label,
                            })}
                          >
                            <Chip
                              size="small"
                              label={empresaBadge}
                              sx={{
                                width: 29,
                                fontSize: "1rem",
                                height: 29,
                                bgcolor: empresaMeta.chipBgcolor,
                                color: empresaMeta.chipColor,
                                fontWeight: 700,
                              }}
                            />
                          </Tooltip>

                          <Tooltip title={t("produccionSummary.editOrder")}>
                            <span>
                              <IconButton
                                size="small"
                                sx={{
                                  p: 0.5,
                                  color: "primary.main",
                                  border: "1px solid",
                                  borderColor: "primary.main",
                                  borderRadius: 1.2,
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.08,
                                  ),
                                }}
                                onClick={() => onEditRow?.(row)}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              bloqueadoBorrado
                                ? t("produccionSummary.deleteServed")
                                : bloqueadoPorLogistica
                                  ? t("produccionSummary.deleteLinked", {
                                      count: enviosPedidoCount,
                                    })
                                  : t("produccionSummary.deleteDefault")
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                sx={{
                                  p: 0.5,
                                  color: "error.main",
                                  border: "1px solid",
                                  borderColor: "error.main",
                                  borderRadius: 1.2,
                                  backgroundColor: alpha(
                                    theme.palette.error.main,
                                    0.08,
                                  ),
                                }}
                                onClick={() => onDeleteRow?.(row)}
                                disabled={bloqueoDelete}
                              >
                                <DeleteOutlineIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {tieneVenta &&
                          row?.IdPresupuestoA3Pedido != null &&
                          onEnviosRow ? (
                            <Badge
                              color="primary"
                              badgeContent={
                                Number(
                                  enviosCountByPedido?.[
                                    Number(row?.IdPresupuestoA3Pedido)
                                  ] || 0,
                                ) || 0
                              }
                              invisible={
                                !(
                                  Number(
                                    enviosCountByPedido?.[
                                      Number(row?.IdPresupuestoA3Pedido)
                                    ] || 0,
                                  ) > 0
                                )
                              }
                              sx={{ ml: "auto" }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{
                                  minWidth: 0,
                                  px: 1,
                                  py: 0.35,
                                  borderRadius: 1.2,
                                  fontWeight: 700,
                                }}
                                onClick={() => onEnviosRow(row)}
                              >
                                {t("produccionSummary.shipmentsBtn")}
                              </Button>
                            </Badge>
                          ) : null}
                      </Stack>
                    ) : null}

                    <Stack spacing={0.15}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.85rem",
                          display: "block",
                          wordBreak: "break-word",
                          color: "warning.main",
                          fontWeight: 800,
                        }}
                      >
                        {refStr}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.76rem",
                          display: "block",
                          fontWeight: 700,
                          wordBreak: "break-word",
                        }}
                      >
                        {presu}
                      </Typography>
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{
                    width: { xs: "100%", md: A3_COL_COMPRA_WIDTH },
                    display: { xs: "block", md: "table-cell" },
                    borderLeft: { xs: "none", md: "2px solid" },
                    borderTop: { xs: "2px solid", md: "none" },
                    borderColor: tieneCompra ? "error.light" : "divider",
                    bgcolor: tieneCompra
                      ? "rgba(211, 47, 47, 0.04)"
                      : "transparent",
                    ...a3PedidosCellClipSx,
                  }}
                >
                  {tieneCompra ? (
                    <Stack
                      spacing={1.35}
                      mt={1}
                      sx={{
                        p: 1,
                        border: "1px solid",
                        borderColor: alpha(theme.palette.error.main, 0.22),
                        borderRadius: 1.5,
                        backgroundColor: alpha(theme.palette.error.main, 0.04),
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        sx={{ flexWrap: "wrap", rowGap: 0.6 }}
                      >
                        {estadoCompra ? (
                          <Box
                            component="span"
                            sx={{
                              ...a3EstadoBadgeSx(
                                row.EstadoCompra,
                                row.EstadoDetalleCompra,
                              ),
                              height: 24,
                              width: 24,
                              alignItems: "center",
                              display: "flex",
                              justifyContent: "center",
                              borderRadius: 50,
                              fontSize: "0.8rem",
                              fontWeight: 700,
                              mr: 1,
                            }}
                          >
                            {estadoCompra}
                          </Box>
                        ) : null}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            display: "block",
                            mr: 1,
                            color: "#000",
                            backgroundColor: "#ef53508f",
                            px: 0.45,
                            py: 0.1,
                            borderRadius: 0.5,
                          }}
                        >
                          {pcom || "—"}
                        </Typography>
                        <PedidoCompraPrint row={row} />
                        <Box
                          component="span"
                          sx={{
                            minWidth: 78,
                            textAlign: "center",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            bgcolor: "rgba(0,0,0,0.04)",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 0.5,
                            px: 0.5,
                            py: 0.35,
                            mr: 1.2,
                          }}
                        >
                          {fechaCompraStr}
                        </Box>
                        {monedaCompra ? (
                          <Box
                            component="span"
                            sx={{
                              bgcolor: "#ef53508f",
                              color: "#000",
                              mr: 1,
                              height: 24,
                              width: 24,
                              alignItems: "center",
                              display: "flex",
                              justifyContent: "center",
                              borderRadius: 50,
                              fontSize: "0.9rem",
                              fontWeight: 700,
                            }}
                          >
                            {monedaCompra}
                          </Box>
                        ) : null}

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.76rem",
                            display: "block",
                            wordBreak: "break-word",
                            fontWeight: 800,
                            mr: 2,
                          }}
                        >
                          {truncA3(proveedorR, 25)}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.78rem",
                            display: "block",
                            mr: 2,
                          }}
                        >
                          {t("produccionSummary.unitsShort")}
                          {fmtA3Cantidad(row.UnidadesCompra)} ×{" "}
                          {fmtA3Importe(precioCompraVista)} →{" "}
                          <strong>{fmtA3Importe(row.TotalCompra)}</strong>
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.73rem", display: "block" }}
                        >
                          {t("produccionSummary.vatPurchase", {
                            ivaPct: fmtA3PorcIva(row.PorIvaCompra),
                            ivaAmt: fmtA3Importe(row.CuotaIvaCompra),
                          })}{" "}
                          <Box
                            component="span"
                            sx={{
                              bgcolor: "error.main",
                              color: "common.white",
                              fontWeight: 700,
                              px: 0.45,
                              py: 0.15,
                              borderRadius: 0.5,
                              display: "inline-block",
                              fontSize: ".9rem",
                              textAlign: "right",
                            }}
                          >
                            {fmtA3Importe(row.TotalDocumentoCompra)}
                          </Box>
                        </Typography>
                      </Box>
                    </Stack>
                  ) : null}
                </TableCell>
                <TableCell
                  sx={{
                    width: { xs: "100%", md: A3_COL_VENTA_WIDTH },
                    display: { xs: "block", md: "table-cell" },
                    borderLeft: { xs: "none", md: "2px solid" },
                    borderTop: { xs: "2px solid", md: "none" },
                    borderColor: tieneVenta ? "primary.main" : "divider",
                    bgcolor: tieneVenta
                      ? "rgba(0, 194, 163, 0.06)"
                      : "transparent",
                    ...a3PedidosCellClipSx,
                  }}
                >
                  {tieneVenta ? (
                    <Stack
                      spacing={1.35}
                      mt={1}
                      sx={{
                        p: 1,
                        border: "1px solid",
                        borderColor: alpha(theme.palette.primary.main, 0.24),
                        borderRadius: 1.5,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.045,
                        ),
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        sx={{ flexWrap: "wrap", rowGap: 0.6 }}
                      >
                        {estadoVenta ? (
                          <Box
                            component="span"
                            sx={{
                              ...a3EstadoBadgeSx(
                                row.EstadoVenta,
                                row.EstadoDetalleVenta,
                              ),
                              height: 24,
                              width: 24,
                              alignItems: "center",
                              display: "flex",
                              justifyContent: "center",
                              borderRadius: 50,
                              fontSize: "0.8rem",
                              fontWeight: 700,
                              mr: 1,
                            }}
                          >
                            {estadoVenta}
                          </Box>
                        ) : null}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            display: "block",
                            color: "#000",
                            mr: 2,
                            backgroundColor: "primary.light",
                            px: 0.45,
                            py: 0.1,
                            borderRadius: 0.5,
                          }}
                        >
                          {pvta || "—"}
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            minWidth: 78,
                            textAlign: "center",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            color: "text.secondary",
                            bgcolor: "rgba(0,0,0,0.04)",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 0.5,
                            px: 0.5,
                            py: 0.35,
                            mr: 1.2,
                          }}
                        >
                          {fechaVentaStr}
                        </Box>
                        {monedaVenta ? (
                          <Box
                            component="span"
                            sx={{
                              bgcolor: "primary.main",
                              color: "#fff",
                              height: 24,
                              width: 24,
                              alignItems: "center",
                              display: "flex",
                              justifyContent: "center",
                              borderRadius: 50,
                              fontSize: "0.9rem",
                              fontWeight: 700,
                              mr: 1,
                            }}
                          >
                            {monedaVenta}
                          </Box>
                        ) : null}

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.76rem",
                            display: "block",
                            wordBreak: "break-word",
                            fontWeight: 800,
                            mr: 2,
                          }}
                        >
                          {truncA3(clienteR, 35)}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.78rem",
                            display: "block",
                            mr: 2,
                          }}
                        >
                          {t("produccionSummary.unitsShort")}
                          {fmtA3Cantidad(row.UnidadesVenta)} ×{" "}
                          {fmtA3Importe(precioVentaVista)} →{" "}
                          <strong>{fmtA3Importe(row.TotalVenta)}</strong>
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.73rem", display: "block" }}
                        >
                          {t("produccionSummary.vatSale", {
                            ivaPct: fmtA3PorcIva(row.PorIvaVenta),
                            ivaAmt: fmtA3Importe(row.CuotaIvaVenta),
                          })}{" "}
                          <Box
                            component="span"
                            sx={{
                              bgcolor: "#00c2a3",
                              color: "#fff",
                              fontWeight: 800,
                              px: 0.45,
                              py: 0.15,
                              borderRadius: 0.5,
                              display: "inline-block",
                              fontSize: ".9rem",
                            }}
                          >
                            {fmtA3Importe(row.TotalDocumentoVenta)}
                          </Box>
                        </Typography>
                      </Box>
                    </Stack>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const ProduccionSummary = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const datePickerLocale = useMemo(
    () => dateFnsLocaleFromI18n(i18n.language),
    [i18n.language],
  );
  const { contentMargin } = useContext(SidebarContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pedidoCrmEditando, setPedidoCrmEditando] = useState(null);
  const [pedidoCrmCreandoBase, setPedidoCrmCreandoBase] = useState(null);
  const [pedidosDialogOpen, setPedidosDialogOpen] = useState(false);
  const [permisoPedidosA3, setPermisoPedidosA3] = useState(false);
  const [filtroGlobal, setFiltroGlobal] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroEnvio, setFiltroEnvio] = useState("");
  const [filtroFechaActivo, setFiltroFechaActivo] = useState(true);
  const [filtroFechaRango, setFiltroFechaRango] = useState(() => {
    const hoy = startOfToday();
    return [hoy, hoy];
  });
  const [filtroFechaCampo, setFiltroFechaCampo] = useState("ambos");
  const [ordenDireccion, setOrdenDireccion] = useState("desc");
  const [deleteA3DialogOpen, setDeleteA3DialogOpen] = useState(false);
  const [deleteA3DialogRow, setDeleteA3DialogRow] = useState(null);
  const [deleteA3DialogError, setDeleteA3DialogError] = useState("");
  const [deleteA3DialogLoading, setDeleteA3DialogLoading] = useState(false);
  const [unitsLimitDialogOpen, setUnitsLimitDialogOpen] = useState(false);
  const [unitsLimitDialogMessage, setUnitsLimitDialogMessage] = useState("");
  const [pendingOverUnitsCreate, setPendingOverUnitsCreate] = useState(null);
  const [enviosDialogCtx, setEnviosDialogCtx] = useState(null);
  const [cpBackfillDialogOpen, setCpBackfillDialogOpen] = useState(false);
  const [cpBackfillLoading, setCpBackfillLoading] = useState(false);
  const [cpBackfillResult, setCpBackfillResult] = useState("");
  const [cpBackfillSuccess, setCpBackfillSuccess] = useState(false);
  const [postPedidoCpDialog, setPostPedidoCpDialog] = useState({
    open: false,
    productId: null,
    idPresupuestoA3Pedido: null,
    unidadesPrendas: 0,
  });
  const [unitsByPresupuesto, setUnitsByPresupuesto] = useState({});
  const [enviosCountByPedido, setEnviosCountByPedido] = useState({});
  const [selectedPedidoId, setSelectedPedidoId] = useState(null);
  const pedidosLoadSeqRef = useRef(0);

  const fechaDesdeYmd = useMemo(
    () => toYmd(filtroFechaRango?.[0]),
    [filtroFechaRango],
  );
  const fechaHastaYmd = useMemo(
    () => toYmd(filtroFechaRango?.[1]),
    [filtroFechaRango],
  );

  const load = useCallback(async () => {
    const loadSeq = ++pedidosLoadSeqRef.current;
    setLoading(true);
    setError("");
    try {
      const pedidosOpts = { limit: 2000 };
      if (filtroFechaActivo && fechaDesdeYmd && fechaHastaYmd) {
        pedidosOpts.fechaDesde = fechaDesdeYmd;
        pedidosOpts.fechaHasta = fechaHastaYmd;
        pedidosOpts.fechaCampo = filtroFechaCampo;
      }
      const [data, enviosResumen] = await Promise.all([
        getAllPresupuestoA3Pedidos(pedidosOpts),
        getLogisticaEnviosResumen({ limit: 2000 }).catch(() => ({ rows: [] })),
      ]);
      if (loadSeq !== pedidosLoadSeqRef.current) return;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      const enviosRows = Array.isArray(enviosResumen)
        ? enviosResumen
        : Array.isArray(enviosResumen?.rows)
          ? enviosResumen.rows
          : [];
      const byPedidoSets = new Map();
      for (const envio of enviosRows) {
        const idEnvio = Number(envio?.IdLogisticaEnvio);
        if (!Number.isFinite(idEnvio) || idEnvio <= 0) continue;
        const links = Array.isArray(envio?.pedidoLinks)
          ? envio.pedidoLinks
          : [];
        for (const lnk of links) {
          const idPed = Number(
            lnk?.IdPresupuestoA3Pedido ?? lnk?.pedidoA3?.IdPresupuestoA3Pedido,
          );
          if (!Number.isFinite(idPed) || idPed <= 0) continue;
          if (!byPedidoSets.has(idPed)) byPedidoSets.set(idPed, new Set());
          byPedidoSets.get(idPed).add(idEnvio);
        }
      }
      const counts = {};
      for (const [idPed, idsEnvio] of byPedidoSets.entries()) {
        counts[idPed] = idsEnvio.size;
      }
      setEnviosCountByPedido(counts);
    } catch (e) {
      if (loadSeq !== pedidosLoadSeqRef.current) return;
      setRows([]);
      setEnviosCountByPedido({});
      setError(
        e?.response?.data?.error ||
          e.message ||
          t("produccionSummary.errorLoad"),
      );
    } finally {
      if (loadSeq === pedidosLoadSeqRef.current) {
        setLoading(false);
      }
    }
  }, [filtroFechaActivo, fechaDesdeYmd, fechaHastaYmd, filtroFechaCampo, t]);

  const parseNum = (v) => {
    const n = Number(String(v ?? "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };

  const puedeEditarPedidosA3 = permisoPedidosA3 === true;

  useEffect(() => {
    const token = localStorage.getItem("token");
    let userId = null;
    if (token) {
      try {
        const decoded = jwtDecode(token);
        userId = decoded.userId;
      } catch {
        /* ignore */
      }
    }
    if (!userId) {
      setPermisoPedidosA3(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const user = await getUserById(userId);
        if (!cancelled) {
          setPermisoPedidosA3(Boolean(user?.Permiso_PedidosA3));
        }
      } catch (e) {
        console.error("Permiso_PedidosA3 (ProduccionSummary):", e);
        if (!cancelled) setPermisoPedidosA3(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDeleteA3Pedido = useCallback(
    (row) => {
      if (!row || !puedeEditarPedidosA3) return;
      if (
        isA3Served(row?.EstadoCompra, row?.EstadoDetalleCompra) ||
        isA3Served(row?.EstadoVenta, row?.EstadoDetalleVenta)
      ) {
        return;
      }
      setDeleteA3DialogRow(row);
      setDeleteA3DialogError("");
      setDeleteA3DialogOpen(true);
    },
    [puedeEditarPedidosA3],
  );

  const confirmDeleteA3Pedido = useCallback(async () => {
    if (!deleteA3DialogRow) return;
    setDeleteA3DialogLoading(true);
    setDeleteA3DialogError("");
    try {
      await deleteA3Pedidos({
        idPresupuestoA3Pedido: deleteA3DialogRow.IdPresupuestoA3Pedido,
        idPedc: deleteA3DialogRow.IdPedc,
        idPedv: deleteA3DialogRow.IdPedv,
        idLinCompra: deleteA3DialogRow.IdLinCompra,
        idLinVenta: deleteA3DialogRow.IdLinVenta,
        codart: deleteA3DialogRow.CodArt,
        serieCompra: deleteA3DialogRow.SerieCompra,
        numDocCompra: deleteA3DialogRow.NumDocCompra,
        serieVenta: deleteA3DialogRow.SerieVenta,
        numDocVenta: deleteA3DialogRow.NumDocVenta,
        dbSource: String(deleteA3DialogRow.EmpresaA3 || "").trim() || undefined,
      });
      setDeleteA3DialogOpen(false);
      setDeleteA3DialogRow(null);
      await load();
    } catch (e) {
      setDeleteA3DialogError(
        e?.response?.data?.error ||
          e?.message ||
          t("produccionSummary.errorDelete"),
      );
    } finally {
      setDeleteA3DialogLoading(false);
    }
  }, [deleteA3DialogRow, load, t]);

  const closeDeleteA3Dialog = useCallback(() => {
    if (deleteA3DialogLoading) return;
    setDeleteA3DialogOpen(false);
    setDeleteA3DialogRow(null);
    setDeleteA3DialogError("");
  }, [deleteA3DialogLoading]);

  useEffect(() => {
    load();
  }, [load]);

  const pedidosContextRowsForDialog = useMemo(() => {
    const idPres = Number(
      pedidoCrmEditando?.IdPresupuesto ??
        pedidoCrmCreandoBase?.IdPresupuesto ??
        0,
    );
    if (!(idPres > 0)) return rows;
    return rows.filter((r) => Number(r?.IdPresupuesto || 0) === idPres);
  }, [rows, pedidoCrmEditando, pedidoCrmCreandoBase]);

  const referenciaPedidoSuffix = useMemo(
    () => nextReferenciaPedidoSuffix(pedidosContextRowsForDialog),
    [pedidosContextRowsForDialog],
  );

  const rowsFiltradasYOrdenadas = useMemo(() => {
    const filtered = rows.filter((row) => {
      const q = normA3(filtroGlobal);
      const okGlobal =
        !q ||
        [
          row?.CodArt,
          row?.Referencia,
          getClienteA3(row),
          getPoCompraA3(row),
          getPoVentaA3(row),
          row?.NumDocCompra,
          row?.NumDocVenta,
          row?.IdPedidoCompra,
          row?.IdPedidoVenta,
          row?.IdPresupuestoA3Pedido,
          row?.IdPresupuesto,
          row?.IdProyecto,
          row?.EmpresaA3,
        ]
          .map((v) => normA3(v))
          .some((v) => v.includes(q));
      const empresaRow = normalizeEmpresaA3(row?.EmpresaA3, "");
      const okEmpresa = !filtroEmpresa || empresaRow === filtroEmpresa;
      const enviosPedido = getPedidoEnviosCount(row, enviosCountByPedido);
      const tieneVenta = rowTieneVentaA3(row);
      const okEnvio =
        !filtroEnvio ||
        (filtroEnvio === "pendientes" && tieneVenta && enviosPedido === 0) ||
        (filtroEnvio === "con-envios" && tieneVenta && enviosPedido > 0) ||
        (filtroEnvio === "sin-venta" && !tieneVenta);
      return okGlobal && okEmpresa && okEnvio;
    });

    const getSortValue = (row) => {
      const refFecha =
        row.FechaVentaA3 || row.FechaCompraA3 || row.FechaA3 || row.CreatedAt;
      const t = new Date(refFecha).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    const dir = ordenDireccion === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = getSortValue(a);
      const bv = getSortValue(b);
      if (typeof av === "number" && typeof bv === "number")
        return (av - bv) * dir;
      return (
        String(av).localeCompare(String(bv), "es", { sensitivity: "base" }) *
        dir
      );
    });
  }, [
    rows,
    filtroGlobal,
    filtroEmpresa,
    filtroEnvio,
    ordenDireccion,
    enviosCountByPedido,
  ]);

  const grupos = useMemo(
    () => agruparPorArticulo(rowsFiltradasYOrdenadas),
    [rowsFiltradasYOrdenadas],
  );
  const clienteTreeGroups = useMemo(
    () =>
      groupProduccionPedidosByCliente(rowsFiltradasYOrdenadas, {
        sinCliente: t("produccionSummary.treeSinCliente"),
      }).map((group) => ({
        key: group.key,
        label: group.label,
        badge: group.empresaShort || "",
        accent: getProduccionEmpresaTreeAccent(group.empresa),
        items: (group.articulos || []).map(({ codArt, pedidos }) => ({
          key: `${group.key}::${codArt}`,
          label:
            codArt === NO_ARTICLE_CODE
              ? t("produccionSummary.noArticleCode")
              : codArt,
          count: pedidos.length,
          items: (pedidos || [])
            .map((row) => {
              const id = getProduccionPedidoTreeId(row);
              const envios = getPedidoEnviosCount(row, enviosCountByPedido);
              const tieneVenta = rowTieneVentaA3(row);
              return {
                id,
                label: getProduccionPedidoTreeLabel(row),
                date: getProduccionPedidoTreeDate(row),
                count: envios,
                closed: !tieneVenta || envios > 0,
              };
            })
            .filter((item) => item.id),
        })).filter((art) => art.items.length),
      })),
    [rowsFiltradasYOrdenadas, enviosCountByPedido, t],
  );

  useEffect(() => {
    const ids = clienteTreeGroups.flatMap((g) =>
      (g.items || []).flatMap((item) =>
        Array.isArray(item.items)
          ? item.items.map((leaf) => Number(leaf.id))
          : [Number(item.id)],
      ),
    );
    if (!ids.length) {
      setSelectedPedidoId(null);
      return;
    }
    if (!ids.includes(Number(selectedPedidoId))) {
      setSelectedPedidoId(ids[0]);
    }
  }, [clienteTreeGroups, selectedPedidoId]);

  const grupoSeleccionado = useMemo(
    () => getProduccionGrupoDePedido(rowsFiltradasYOrdenadas, selectedPedidoId),
    [rowsFiltradasYOrdenadas, selectedPedidoId],
  );
  const gruposDetalle = grupoSeleccionado ? [grupoSeleccionado] : [];
  const resumenEnviosFiltrado = useMemo(() => {
    let pedidosVenta = 0;
    let pedidosConEnvio = 0;
    let pedidosPendientes = 0;
    let enviosTotales = 0;
    for (const row of rowsFiltradasYOrdenadas) {
      if (!rowTieneVentaA3(row)) continue;
      pedidosVenta += 1;
      const count = getPedidoEnviosCount(row, enviosCountByPedido);
      enviosTotales += count;
      if (count > 0) pedidosConEnvio += 1;
      else pedidosPendientes += 1;
    }
    return { pedidosVenta, pedidosConEnvio, pedidosPendientes, enviosTotales };
  }, [rowsFiltradasYOrdenadas, enviosCountByPedido]);

  const hoyYmd = useMemo(() => toYmd(startOfToday()), []);
  const filtrosLocalesActivos = useMemo(() => {
    const chips = [];
    if (filtroGlobal.trim()) {
      chips.push({
        key: "global",
        label: t("produccionSummary.chipSearch", { q: filtroGlobal.trim() }),
        onDelete: () => setFiltroGlobal(""),
      });
    }
    if (filtroEmpresa) {
      const meta = getEmpresaA3Meta(filtroEmpresa);
      chips.push({
        key: "empresa",
        label: t("produccionSummary.chipCompany", {
          name: meta?.label || filtroEmpresa,
        }),
        onDelete: () => setFiltroEmpresa(""),
      });
    }
    if (filtroEnvio) {
      const envLabels = {
        pendientes: t("produccionSummary.envPending"),
        "con-envios": t("produccionSummary.envWithShipments"),
        "sin-venta": t("produccionSummary.envNoSale"),
      };
      chips.push({
        key: "envio",
        label: t("produccionSummary.chipShipments", {
          label: envLabels[filtroEnvio] || filtroEnvio,
        }),
        onDelete: () => setFiltroEnvio(""),
      });
    }
    if (ordenDireccion === "asc") {
      chips.push({
        key: "orden",
        label: t("produccionSummary.chipOrderAsc"),
        onDelete: () => setOrdenDireccion("desc"),
      });
    }
    if (filtroFechaCampo !== "ambos") {
      const campoLabels = {
        compra: t("produccionSummary.dateFieldPurchase"),
        venta: t("produccionSummary.dateFieldSale"),
      };
      chips.push({
        key: "fechaCampo",
        label: t("produccionSummary.chipDateField", {
          field: campoLabels[filtroFechaCampo] || filtroFechaCampo,
        }),
        onDelete: () => setFiltroFechaCampo("ambos"),
      });
    }
    if (!filtroFechaActivo) {
      chips.push({
        key: "fechaHistorico",
        label: t("produccionSummary.chipHistoric"),
        onDelete: () => {
          setFiltroFechaActivo(true);
          const hoy = startOfToday();
          setFiltroFechaRango([hoy, hoy]);
        },
      });
    } else if (fechaDesdeYmd !== hoyYmd || fechaHastaYmd !== hoyYmd) {
      chips.push({
        key: "fechas",
        label: t("produccionSummary.chipRange", {
          from: fechaDesdeYmd || "—",
          to: fechaHastaYmd || "—",
        }),
        onDelete: () => {
          const hoy = startOfToday();
          setFiltroFechaRango([hoy, hoy]);
        },
      });
    }
    return chips;
  }, [
    filtroGlobal,
    filtroEmpresa,
    filtroEnvio,
    ordenDireccion,
    filtroFechaCampo,
    filtroFechaActivo,
    fechaDesdeYmd,
    fechaHastaYmd,
    hoyYmd,
    t,
  ]);

  const limpiarFiltrosLocales = useCallback(() => {
    setFiltroGlobal("");
    setFiltroEmpresa("");
    setFiltroEnvio("");
    setOrdenDireccion("desc");
    const hoy = startOfToday();
    setFiltroFechaActivo(true);
    setFiltroFechaRango([hoy, hoy]);
    setFiltroFechaCampo("ambos");
  }, []);

  const canCreatePedidoA3 =
    puedeEditarPedidosA3 && rowsFiltradasYOrdenadas.length > 0;
  const runCreatePedido = useCallback(
    async (_pedidoPayload) => {
      const modoAlta = _pedidoPayload?.modo || "ambos";
      const soloVentaAlta = modoAlta === "venta";
      const soloCompraAlta = modoAlta === "compra";
      const payloadAlta = {
        idPresupuesto: Number(pedidoCrmCreandoBase?.IdPresupuesto || 0),
        idProyecto: Number(pedidoCrmCreandoBase?.IdProyecto || 0),
        modo: modoAlta,
        codart: String(_pedidoPayload?.articulo || "").trim(),
        codpro: String(_pedidoPayload?.proveedor || "").trim(),
        codcli: String(_pedidoPayload?.cliente || "").trim(),
        clienteNombre: String(_pedidoPayload?.clienteNombre || "").trim(),
        proveedorNombre: String(_pedidoPayload?.proveedorNombre || "").trim(),
        referencia: _pedidoPayload?.referencia,
        unidadesCompra: soloVentaAlta
          ? 0
          : parseNum(_pedidoPayload?.unidadesCompra),
        precioCoste: soloVentaAlta ? 0 : parseNum(_pedidoPayload?.precioCoste),
        codMonCompra: String(_pedidoPayload?.monedaCompra || "").trim(),
        unidadesVenta: soloCompraAlta
          ? 0
          : parseNum(_pedidoPayload?.unidadesVenta),
        precioVenta: soloCompraAlta ? 0 : parseNum(_pedidoPayload?.precioVenta),
        codMonVenta: String(_pedidoPayload?.monedaVenta || "").trim(),
        tipoContable: String(_pedidoPayload?.tipoContable || "1").trim(),
        dbSource: String(_pedidoPayload?.dbSource || "STAGE_DESIGN").trim(),
        lineasTallas: Array.isArray(_pedidoPayload?.lineasTallas)
          ? _pedidoPayload.lineasTallas
          : [],
        fecha: normalizeFechaIso(_pedidoPayload?.fecha),
        centroCosteEmpresa: String(
          _pedidoPayload?.centroCosteEmpresa || "",
        ).trim(),
        centroCosteMarca: String(_pedidoPayload?.centroCosteMarca || "").trim(),
        centroCosteDepartamento: String(
          _pedidoPayload?.centroCosteDepartamento || "",
        ).trim(),
      };
      const resultadoAlta = await createA3Pedidos(payloadAlta);
      const optimisticRow = buildOptimisticA3PedidoRow(
        payloadAlta,
        resultadoAlta,
      );
      if (optimisticRow) {
        setRows((prev) => {
          const id = Number(optimisticRow.IdPresupuestoA3Pedido);
          const rest = (Array.isArray(prev) ? prev : []).filter(
            (r) => Number(r?.IdPresupuestoA3Pedido) !== id,
          );
          const enriched = {
            ...optimisticRow,
            ...(pedidoCrmCreandoBase?.NombrePresupuesto
              ? { NombrePresupuesto: pedidoCrmCreandoBase.NombrePresupuesto }
              : {}),
            ...(pedidoCrmCreandoBase?.IdProducto
              ? { IdProducto: pedidoCrmCreandoBase.IdProducto }
              : {}),
            Units:
              pedidoCrmCreandoBase?.UnitsEstimadas ??
              pedidoCrmCreandoBase?.Units,
          };
          return [enriched, ...rest];
        });
      }
      const crmId = resultadoAlta?.crmLink?.crmId;
      const productIdCp = Number(pedidoCrmCreandoBase?.IdProducto);
      const unidadesPrendas = computePrendasUnidadesPedido(
        _pedidoPayload,
        String(modoAlta || "ambos")
          .trim()
          .toLowerCase(),
      );
      await load();
      setPedidosDialogOpen(false);
      setPedidoCrmCreandoBase(null);
      if (
        crmId &&
        Number.isFinite(productIdCp) &&
        productIdCp > 0 &&
        unidadesPrendas > 0 &&
        modoAlta !== "compra"
      ) {
        setPostPedidoCpDialog({
          open: true,
          productId: productIdCp,
          idPresupuestoA3Pedido: crmId,
          unidadesPrendas,
        });
      }
    },
    [pedidoCrmCreandoBase, load],
  );
  const ensureUnitsPresupuesto = useCallback(
    async (idProyecto, idPresupuesto, fallback = 0) => {
      const proj = Number(idProyecto || 0);
      const pres = Number(idPresupuesto || 0);
      if (!(proj > 0 && pres > 0)) return Number(fallback || 0);
      const key = `${proj}:${pres}`;
      if (Object.prototype.hasOwnProperty.call(unitsByPresupuesto, key)) {
        return Number(unitsByPresupuesto[key] || 0);
      }
      try {
        const data = await getPresupuestosByProyectoId(proj);
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.rows)
            ? data.rows
            : Array.isArray(data?.presupuestos)
              ? data.presupuestos
              : [];
        const hit = list.find((p) => Number(p?.IdPresupuesto || 0) === pres);
        const units = Number(hit?.Units || 0);
        const resolved =
          Number.isFinite(units) && units > 0 ? units : Number(fallback || 0);
        setUnitsByPresupuesto((prev) => ({ ...prev, [key]: resolved }));
        return resolved;
      } catch (_e) {
        return Number(fallback || 0);
      }
    },
    [unitsByPresupuesto],
  );
  const handleOpenEditPedido = useCallback(
    async (row) => {
      if (!puedeEditarPedidosA3 || !row) return;
      const fallbackUnits = Math.max(
        Number(row?.Units || 0),
        Number(row?.UnidadesVenta || 0),
        Number(row?.UnidadesCompra || 0),
        0,
      );
      const units = await ensureUnitsPresupuesto(
        row?.IdProyecto,
        row?.IdPresupuesto,
        fallbackUnits,
      );
      setPedidoCrmCreandoBase(null);
      setPedidoCrmEditando({ ...row, UnitsEstimadas: units });
      setPedidosDialogOpen(true);
    },
    [ensureUnitsPresupuesto, puedeEditarPedidosA3],
  );
  const handleOpenCreatePedido = useCallback(() => {
    if (!canCreatePedidoA3) return;
    const base = rowsFiltradasYOrdenadas[0] || null;
    if (!base) return;
    (async () => {
      const fallbackUnits = Math.max(
        Number(base?.Units || 0),
        Number(base?.UnidadesVenta || 0),
        Number(base?.UnidadesCompra || 0),
        0,
      );
      const units = await ensureUnitsPresupuesto(
        base?.IdProyecto,
        base?.IdPresupuesto,
        fallbackUnits,
      );
      setPedidoCrmEditando(null);
      setPedidoCrmCreandoBase({ ...base, UnitsEstimadas: units });
      setPedidosDialogOpen(true);
    })();
  }, [canCreatePedidoA3, rowsFiltradasYOrdenadas, ensureUnitsPresupuesto]);

  const handleRunCpBackfill = useCallback(async () => {
    if (!puedeEditarPedidosA3 || cpBackfillLoading) return;
    setCpBackfillLoading(true);
    setCpBackfillResult("");
    setCpBackfillSuccess(false);
    try {
      const result = await backfillCriticalPathFromPedidos();
      const links = Number(result?.insertedCpLinks || 0);
      const timelines = Number(result?.insertedTimelineRows || 0);
      setCpBackfillSuccess(true);
      setCpBackfillResult(
        t("produccionSummary.cpBackfillSuccess", { links, timelines }),
      );
      await load();
    } catch (e) {
      setCpBackfillSuccess(false);
      setCpBackfillResult(
        e?.response?.data?.error ||
          e?.message ||
          t("produccionSummary.cpBackfillError"),
      );
    } finally {
      setCpBackfillLoading(false);
      setCpBackfillDialogOpen(false);
    }
  }, [cpBackfillLoading, puedeEditarPedidosA3, load, t]);

  const accent = theme.palette.primary.main;
  const filterFieldSx = {
    ...pageFieldSx(theme),
    width: "100%",
    minWidth: 0,
  };
  const toolbarItemSx = {
    ...filterFieldSx,
    flex: "1 1 120px",
    maxWidth: { sm: 168 },
  };
  const kpiChipSx = {
    ...pageKpiChipSx,
    borderRadius: 1.25,
    "& .MuiChip-label": { px: 1, fontSize: "0.72rem" },
  };
  const heroShellSx = {
    ...pageHeroShellSx(theme, { sticky: false }),
    mb: { xs: 1.5, md: 1 },
    ...(theme.palette.mode === "light"
      ? {
          background: theme.palette.background.paper,
          borderColor: alpha(accent, 0.2),
          boxShadow: `0 10px 32px ${alpha(theme.palette.common.black, 0.08)}`,
        }
      : {}),
  };
  const toolbarIconSx = {
    border: "1px solid",
    borderColor: alpha(accent, 0.22),
    backgroundColor: theme.palette.background.paper,
    color: accent,
    width: { xs: 40, md: 36 },
    height: { xs: 40, md: 36 },
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
    "&:hover": {
      backgroundColor: alpha(accent, 0.1),
      borderColor: accent,
    },
  };
  const mobileActionGridSx = {
    display: "grid",
    gridTemplateColumns: puedeEditarPedidosA3
      ? "repeat(3, minmax(0, 1fr))"
      : "repeat(2, minmax(0, 1fr))",
    gap: 0.75,
    p: 1,
    borderRadius: 2.5,
    border: "1px solid",
    borderColor: alpha(accent, 0.18),
    bgcolor: theme.palette.background.paper,
    boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, 0.06)}`,
    "& .MuiIconButton-root, & > span": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  };
  const filtersPanelSx = {
    ...pageContentCardSx(theme, accent, { highlighted: false }),
    p: { xs: 1.25, md: 1 },
    display: "flex",
    flexWrap: "wrap",
    gap: { xs: 0.75, md: 0.625 },
    alignItems: "flex-end",
  };

  const subtitleText = loading
    ? t("produccionSummary.loading")
    : `${t("produccionSummary.orderCount", {
        count: rowsFiltradasYOrdenadas.length,
      })}${
        !filtroFechaActivo
          ? t("produccionSummary.historicSuffix")
          : fechaDesdeYmd && fechaHastaYmd
            ? t("produccionSummary.dateRangeSuffix", {
                from: fechaDesdeYmd,
                to: fechaHastaYmd,
              })
            : ""
      }`;

  const kpiChipsRow = (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: { xs: 0, md: 0.5 } }}>
      <Chip
        size="small"
        icon={
          <LocalShippingOutlinedIcon sx={{ fontSize: "0.9rem !important" }} />
        }
        label={t("produccionSummary.kpiSale", {
          count: resumenEnviosFiltrado.pedidosVenta,
        })}
        sx={{
          ...kpiChipSx,
          bgcolor: alpha(theme.palette.info.main, 0.14),
          color: "info.dark",
          border: "1px solid",
          borderColor: alpha(theme.palette.info.main, 0.22),
        }}
      />
      <Chip
        size="small"
        label={t("produccionSummary.kpiShipment", {
          count: resumenEnviosFiltrado.pedidosConEnvio,
        })}
        sx={{
          ...kpiChipSx,
          bgcolor: alpha(theme.palette.success.main, 0.14),
          color: "success.dark",
          border: "1px solid",
          borderColor: alpha(theme.palette.success.main, 0.22),
        }}
      />
      <Chip
        size="small"
        label={t("produccionSummary.kpiPending", {
          count: resumenEnviosFiltrado.pedidosPendientes,
        })}
        sx={{
          ...kpiChipSx,
          bgcolor: alpha(
            resumenEnviosFiltrado.pedidosPendientes > 0
              ? theme.palette.error.main
              : theme.palette.success.main,
            0.14,
          ),
          color:
            resumenEnviosFiltrado.pedidosPendientes > 0
              ? "error.dark"
              : "success.dark",
          border: "1px solid",
          borderColor: alpha(
            resumenEnviosFiltrado.pedidosPendientes > 0
              ? theme.palette.error.main
              : theme.palette.success.main,
            0.22,
          ),
        }}
      />
      <Chip
        size="small"
        label={t("produccionSummary.kpiShipmentsTotal", {
          count: resumenEnviosFiltrado.enviosTotales,
        })}
        sx={{
          ...kpiChipSx,
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          color: "primary.dark",
          border: "1px solid",
          borderColor: alpha(theme.palette.primary.main, 0.2),
        }}
      />
    </Stack>
  );

  const desktopActionsRow = (
    <Stack
      direction="row"
      spacing={0.75}
      useFlexGap
      flexWrap="nowrap"
      sx={{ flexShrink: 0, alignSelf: { md: "flex-start" } }}
    >
      <Tooltip title={t("produccionSummary.refresh")}>
        <span>
          <IconButton onClick={load} disabled={loading} sx={toolbarIconSx}>
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      {puedeEditarPedidosA3 ? (
        <Tooltip
          title={
            cpBackfillLoading
              ? t("produccionSummary.reconcilingCp")
              : t("produccionSummary.reconcileCp")
          }
        >
          <span>
            <IconButton
              onClick={() => setCpBackfillDialogOpen(true)}
              disabled={cpBackfillLoading}
              aria-label={t("produccionSummary.reconcileCpAria")}
              sx={{
                ...toolbarIconSx,
                color: theme.palette.warning.dark,
                borderColor: alpha(theme.palette.warning.main, 0.45),
              }}
            >
              {cpBackfillLoading ? (
                <CircularProgress size={16} color="warning" />
              ) : (
                <SyncOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
      <Tooltip
        title={
          canCreatePedidoA3
            ? t("produccionSummary.createOrderTooltip")
            : t("produccionSummary.noRowsCreateTooltip")
        }
      >
        <span>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreatePedido}
            disabled={!canCreatePedidoA3}
            sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
          >
            {t("produccionSummary.newA3")}
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { xs: "auto", md: "100dvh" },
        minHeight: { xs: "100dvh", md: 0 },
        boxSizing: "border-box",
        background:
          theme.palette.mode === "dark"
            ? theme.palette.background.default
            : theme.palette.grey[50],
        pt: { xs: 1, md: 1.25 },
        px: { xs: 1.25, sm: 2 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 1.25 },
        minWidth: 0,
        overflow: { xs: "visible", md: "hidden" },
        ...contentMargin,
      }}
    >
      <Box sx={{ ...heroShellSx, flexShrink: 0 }}>
        <Stack spacing={{ xs: 1.25, md: 0.875 }} sx={{ p: { xs: 1.25, md: 1, lg: 1.25 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { md: "flex-start" },
              justifyContent: "space-between",
              gap: { xs: 1.25, md: 1 },
            }}
          >
            <Stack
              direction="row"
              spacing={{ xs: 1.25, md: 1 }}
              alignItems="flex-start"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Box
                sx={{
                  width: { xs: 44, md: 36 },
                  height: { xs: 44, md: 36 },
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: alpha(accent, 0.12),
                  color: theme.palette.primary.dark,
                  border: "1px solid",
                  borderColor: alpha(accent, 0.24),
                }}
              >
                <LibraryBooksOutlinedIcon sx={{ fontSize: { xs: "1.25rem", md: "1.1rem" } }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    ...pageSectionTitleSx,
                    fontSize: { xs: "0.68rem", md: "0.62rem" },
                    lineHeight: 1.1,
                  }}
                >
                  {t("produccionSummary.heroOverline")}
                </Typography>
                <Typography
                  variant={isMobile ? "h5" : "h6"}
                  component="h1"
                  sx={{ fontWeight: 800, lineHeight: 1.1 }}
                >
                  {t("sidebar.produccionSummary")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.25 }}
                >
                  {subtitleText}
                </Typography>
                {!isMobile ? kpiChipsRow : null}
              </Box>
            </Stack>
            {!isMobile ? desktopActionsRow : null}
          </Box>

          {isMobile ? (
            <Stack spacing={1.25}>
              {kpiChipsRow}
              <TextField
                size="small"
                placeholder={t("produccionSummary.searchPlaceholder")}
                value={filtroGlobal}
                onChange={(e) => setFiltroGlobal(e.target.value)}
                sx={filterFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={mobileActionGridSx}>
                <Tooltip title={t("produccionSummary.refresh")}>
                  <span>
                    <IconButton onClick={load} disabled={loading} sx={toolbarIconSx}>
                      {loading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <RefreshIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {puedeEditarPedidosA3 ? (
                  <Tooltip
                    title={
                      cpBackfillLoading
                        ? t("produccionSummary.reconcilingCp")
                        : t("produccionSummary.reconcileCp")
                    }
                  >
                    <span>
                      <IconButton
                        onClick={() => setCpBackfillDialogOpen(true)}
                        disabled={cpBackfillLoading}
                        sx={{
                          ...toolbarIconSx,
                          color: theme.palette.warning.dark,
                          borderColor: alpha(theme.palette.warning.main, 0.45),
                        }}
                      >
                        {cpBackfillLoading ? (
                          <CircularProgress size={16} color="warning" />
                        ) : (
                          <SyncOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <span />
                )}
                <Tooltip
                  title={
                    canCreatePedidoA3
                      ? t("produccionSummary.createOrderTooltip")
                      : t("produccionSummary.noRowsCreateTooltip")
                  }
                >
                  <span style={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={handleOpenCreatePedido}
                      disabled={!canCreatePedidoA3}
                      sx={{ fontWeight: 700, whiteSpace: "nowrap", width: "100%" }}
                    >
                      {t("produccionSummary.newA3")}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
          ) : null}

          <Box sx={filtersPanelSx}>
            {!isMobile ? (
            <TextField
              size="small"
              placeholder={t("produccionSummary.searchPlaceholder")}
              value={filtroGlobal}
              onChange={(e) => setFiltroGlobal(e.target.value)}
              sx={{
                ...filterFieldSx,
                flex: { xs: "1 1 100%", md: "1 1 220px" },
                maxWidth: { md: 280 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            ) : null}
            <TextField
              size="small"
              select
              label={t("produccionSummary.filterCompany")}
              value={filtroEmpresa}
              onChange={(e) => setFiltroEmpresa(String(e.target.value || ""))}
              sx={toolbarItemSx}
            >
              <MenuItem value="">{t("produccionSummary.filterAll")}</MenuItem>
              {A3_EMPRESAS_PEDIDO.map((e) => (
                <MenuItem key={e.key} value={e.key}>
                  {e.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label={t("produccionSummary.filterShipments")}
              value={filtroEnvio}
              onChange={(e) => setFiltroEnvio(String(e.target.value || ""))}
              sx={toolbarItemSx}
            >
              <MenuItem value="">
                {t("produccionSummary.filterAllShipments")}
              </MenuItem>
              <MenuItem value="pendientes">
                {t("produccionSummary.filterPending")}
              </MenuItem>
              <MenuItem value="con-envios">
                {t("produccionSummary.filterWithShipments")}
              </MenuItem>
              <MenuItem value="sin-venta">
                {t("produccionSummary.filterNoSale")}
              </MenuItem>
            </TextField>
            <TextField
              size="small"
              select
              label={t("produccionSummary.filterOrder")}
              value={ordenDireccion}
              onChange={(e) => setOrdenDireccion(e.target.value)}
              sx={{ ...toolbarItemSx, maxWidth: { sm: 140 } }}
            >
              <MenuItem value="desc">
                {t("produccionSummary.orderRecent")}
              </MenuItem>
              <MenuItem value="asc">{t("produccionSummary.orderOld")}</MenuItem>
            </TextField>
            <TextField
              size="small"
              select
              label={t("produccionSummary.filterDate")}
              value={filtroFechaCampo}
              onChange={(e) =>
                setFiltroFechaCampo(String(e.target.value || "ambos"))
              }
              sx={toolbarItemSx}
            >
              <MenuItem value="ambos">
                {t("produccionSummary.dateBoth")}
              </MenuItem>
              <MenuItem value="compra">
                {t("produccionSummary.datePurchase")}
              </MenuItem>
              <MenuItem value="venta">
                {t("produccionSummary.dateSale")}
              </MenuItem>
            </TextField>
            <Box
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 190px" },
                minWidth: { md: 180 },
                maxWidth: { md: 240 },
                "& .react-datepicker-wrapper": { width: "100%" },
              }}
            >
              <ReactDatePicker
                selectsRange
                startDate={filtroFechaRango?.[0] ?? null}
                endDate={filtroFechaRango?.[1] ?? null}
                onChange={(dates) => {
                  const [desde, hasta] = dates;
                  setFiltroFechaRango([
                    desde && isValid(desde) ? startOfDay(desde) : null,
                    hasta && isValid(hasta) ? startOfDay(hasta) : null,
                  ]);
                  if (!filtroFechaActivo) setFiltroFechaActivo(true);
                }}
                dateFormat="dd/MM/yyyy"
                locale={datePickerLocale}
                disabled={!filtroFechaActivo}
                isClearable
                placeholderText={t("produccionSummary.dateRangePlaceholder")}
                portalId="produccion-summary-datepicker-portal"
                popperClassName="produccion-summary-datepicker-popper"
                popperProps={{ strategy: "fixed" }}
                customInput={
                  <TextField
                    size="small"
                    fullWidth
                    label={t("produccionSummary.dateRange")}
                    disabled={!filtroFechaActivo}
                    autoComplete="off"
                    inputProps={{ autoComplete: "off" }}
                    sx={filterFieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarMonthOutlinedIcon
                            fontSize="small"
                            color="action"
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                }
              />
            </Box>

            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              alignItems="center"
              flexWrap="nowrap"
              sx={{
                flex: "0 0 auto",
                ml: { md: "auto" },
                pb: 0.15,
              }}
            >
              <Tooltip title={t("produccionSummary.todayTooltip")}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    const hoy = startOfToday();
                    setFiltroFechaActivo(true);
                    setFiltroFechaRango([hoy, hoy]);
                  }}
                  sx={{ minWidth: 48, px: 1.25, fontWeight: 700 }}
                >
                  {t("produccionSummary.today")}
                </Button>
              </Tooltip>
              <Tooltip title={t("produccionSummary.historicTooltip")}>
                <Button
                  size="small"
                  variant={!filtroFechaActivo ? "contained" : "outlined"}
                  startIcon={
                    <HistoryIcon sx={{ fontSize: "1rem !important" }} />
                  }
                  onClick={() => setFiltroFechaActivo(false)}
                  sx={{
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    px: { xs: 1.5, md: 1.25 },
                  }}
                >
                  {t("produccionSummary.historic")}
                </Button>
              </Tooltip>
              <Tooltip title={t("produccionSummary.clearFilters")}>
                <span>
                  <IconButton
                    size="small"
                    onClick={limpiarFiltrosLocales}
                    disabled={filtrosLocalesActivos.length === 0}
                    sx={toolbarIconSx}
                  >
                    <ClearAllIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          {filtrosLocalesActivos.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              sx={{ flexWrap: "wrap", px: 0.25 }}
            >
              {filtrosLocalesActivos.map((chip) => (
                <Chip
                  key={chip.key}
                  size="small"
                  label={chip.label}
                  onDelete={chip.onDelete}
                  sx={{
                    height: 24,
                    maxWidth: "100%",
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  }}
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: { xs: "visible", md: "auto" },
          display: "flex",
          flexDirection: "column",
        }}
      >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {cpBackfillResult ? (
        <Alert
          severity={cpBackfillSuccess ? "success" : "error"}
          sx={{ mb: 2 }}
          onClose={() => {
            setCpBackfillResult("");
            setCpBackfillSuccess(false);
          }}
        >
          {cpBackfillResult}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : grupos.length === 0 ? (
        <Alert severity="info">
          {rows.length > 0
            ? t("produccionSummary.emptyFiltered")
            : !filtroFechaActivo
              ? t("produccionSummary.emptyHistoric")
              : fechaDesdeYmd === hoyYmd && fechaHastaYmd === hoyYmd
                ? t("produccionSummary.emptyToday")
                : t("produccionSummary.emptyRange")}
        </Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(240px, 300px) minmax(0, 1fr)",
            },
            gap: 1.25,
            alignItems: "stretch",
            flex: 1,
            minHeight: { xs: 420, md: 0 },
            height: { md: "100%" },
          }}
        >
          <ClientesNavTree
            groups={clienteTreeGroups}
            selectedId={selectedPedidoId}
            onSelect={setSelectedPedidoId}
            title={t("produccionSummary.treeClientes")}
            countTooltip={(count) =>
              t("produccionSummary.treeEnvios", { count })
            }
            branchCountTooltip={(count) =>
              t("produccionSummary.treePedidos", { count })
            }
            closedLabel={t("produccionSummary.treeConEnvio")}
            openLabel={t("produccionSummary.treePendiente")}
          />
          <Box sx={{ minWidth: 0, minHeight: 0, overflow: "auto" }}>
            {gruposDetalle.map(({ codArt, pedidos }) => {
          const totalesGrupo = totalesPedidosGrupo(pedidos);
          const unitsPedidasGrupo = pedidos.reduce((acc, row) => {
            const uc = Number(row?.UnidadesCompra || 0);
            const uv = Number(row?.UnidadesVenta || 0);
            return acc + Math.max(uc, uv, 0);
          }, 0);
          const unitsByPres = new Map();
          for (const row of pedidos) {
            const idPres = Number(row?.IdPresupuesto || 0);
            const units = Number(row?.Units || 0);
            if (!(idPres > 0) || !(units > 0)) continue;
            if (!unitsByPres.has(idPres)) unitsByPres.set(idPres, units);
          }
          const unitsEstimadasGrupo = Array.from(unitsByPres.values()).reduce(
            (a, b) => a + b,
            0,
          );
          const compraGrupoRow = pedidos.find(
            (p) => p?.IdPedc != null && Number(p.IdPedc) > 0,
          );
          const ventaGrupoRow = pedidos.find(
            (p) => p?.IdPedv != null && Number(p.IdPedv) > 0,
          );
          const monedaCompraGrupo = etiquetaMonedaResumen(
            compraGrupoRow?.MonedaCompraA3 || compraGrupoRow?.MonedaCoste,
          );
          const monedaVentaGrupo = etiquetaMonedaResumen(
            ventaGrupoRow?.MonedaVentaA3 || ventaGrupoRow?.MonedaVenta,
          );
          const pedidosVentaGrupo = pedidos.filter((row) =>
            rowTieneVentaA3(row),
          );
          const pedidosConEnvioGrupo = pedidosVentaGrupo.filter(
            (row) => getPedidoEnviosCount(row, enviosCountByPedido) > 0,
          ).length;
          const pedidosPendientesEnvioGrupo =
            pedidosVentaGrupo.length - pedidosConEnvioGrupo;
          const enviosTotalesGrupo = pedidosVentaGrupo.reduce(
            (acc, row) => acc + getPedidoEnviosCount(row, enviosCountByPedido),
            0,
          );
          const grupoTienePendientes =
            pedidosVentaGrupo.length > 0 && pedidosPendientesEnvioGrupo > 0;
          const margenGrupo =
            totalesGrupo.filasConCompra > 0 && totalesGrupo.filasConVenta > 0
              ? calcResumenMargenGrupo(
                  totalesGrupo.docCompra,
                  totalesGrupo.docVenta,
                  monedaCompraGrupo,
                  monedaVentaGrupo,
                )
              : null;
          const compraImporteStr =
            totalesGrupo.filasConCompra > 0
              ? fmtA3Importe(totalesGrupo.docCompra)
              : "—";
          const ventaImporteStr =
            totalesGrupo.filasConVenta > 0
              ? fmtA3Importe(totalesGrupo.docVenta)
              : "—";
          const margenValorStr = margenGrupo
            ? `${fmtA3MargenPct(margenGrupo.pct)} · ${fmtA3Importe(margenGrupo.abs)}`
            : "—";
          const unitsColor = "#0097a7";
          const enviosColor = grupoTienePendientes ? "#ed6c02" : "#2e7d32";
          const enviosCountColor = "#33ceb5";
          const compraColor = theme.palette.error.main;
          const ventaColor = "#1565c0";
          const margenColor = theme.palette.success.dark;

          return (
            <Accordion
              key={`${codArt}-${pedidos[0]?.IdPresupuestoA3Pedido || "x"}`}
              defaultExpanded
              disableGutters
              square
              sx={{
                mb: 1.25,
                borderRadius: 2.5,
                overflow: "hidden",
                bgcolor: theme.palette.background.paper,
                border: "1px solid",
                borderColor: grupoTienePendientes
                  ? alpha(
                      theme.palette.warning.main,
                      isMobile ? 0.42 : 0.5,
                    )
                  : isMobile
                    ? alpha(theme.palette.divider, 0.75)
                    : alpha(theme.palette.success.main, 0.35),
                boxShadow: isMobile
                  ? grupoTienePendientes
                    ? `0 6px 20px ${alpha(theme.palette.warning.main, 0.12)}`
                    : `0 4px 16px ${alpha(theme.palette.common.black, 0.05)}`
                  : grupoTienePendientes
                    ? `0 2px 10px ${alpha(theme.palette.warning.main, 0.14)}`
                    : `0 2px 10px ${alpha(theme.palette.success.main, 0.08)}`,
                "&::before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={grupoAccordionSummarySx}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.75,
                    width: "100%",
                    minWidth: 0,
                    pr: { xs: 0.5, sm: 1 },
                  }}
                >
                    <Typography
                      variant="subtitle1"
                      fontWeight={800}
                      sx={{ lineHeight: 1.2, mr: 0.25, fontSize: "1.05rem" }}
                    >
                      {codArt === NO_ARTICLE_CODE
                        ? t("produccionSummary.noArticleCode")
                        : codArt}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={700} sx={{ mr: 0.5 }}>
                      {pedidos.length}{" "}
                      {t("produccionSummary.groupOrderLabel", {
                        count: pedidos.length,
                      })}
                    </Typography>
                    <GrupoResumenChip
                      label={t("produccionSummary.estimated")}
                      value={fmtA3Cantidad(unitsEstimadasGrupo)}
                      color={unitsColor}
                    />
                    <GrupoResumenChip
                      label={t("produccionSummary.ordered")}
                      value={fmtA3Cantidad(unitsPedidasGrupo)}
                      color={theme.palette.primary.main}
                    />
                    {unitsEstimadasGrupo > 0 &&
                    unitsPedidasGrupo > unitsEstimadasGrupo ? (
                      <GrupoResumenChip
                        label={t("produccionSummary.excess")}
                        value={`+${fmtA3Cantidad(
                          unitsPedidasGrupo - unitsEstimadasGrupo,
                        )}`}
                        color={theme.palette.error.main}
                      />
                    ) : null}
                    {pedidosVentaGrupo.length > 0 ? (
                      <GrupoResumenChip
                        label={t("produccionSummary.shipmentsDone")}
                        value={`${pedidosConEnvioGrupo}/${pedidosVentaGrupo.length}`}
                        color={enviosColor}
                      />
                    ) : null}
                    <GrupoResumenChip
                      label={t("produccionSummary.shipmentsPending")}
                      value={fmtA3Cantidad(pedidosPendientesEnvioGrupo)}
                      color={grupoTienePendientes ? theme.palette.error.main : "#2e7d32"}
                    />
                    <GrupoResumenChip
                      label={t("produccionSummary.shipmentsCount")}
                      value={fmtA3Cantidad(enviosTotalesGrupo)}
                      color={enviosCountColor}
                    />
                    <GrupoResumenChip
                      label={`${t("produccionSummary.purchase")}${monedaCompraGrupo ? ` ${monedaCompraGrupo}` : ""}`}
                      value={compraImporteStr}
                      color={compraColor}
                    />
                    <GrupoResumenChip
                      label={`${t("produccionSummary.sale")}${monedaVentaGrupo ? ` ${monedaVentaGrupo}` : ""}`}
                      value={ventaImporteStr}
                      color={ventaColor}
                    />
                    <GrupoResumenChip
                      label={t("produccionSummary.margin")}
                      value={margenValorStr}
                      color={margenColor}
                    />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <TablaPedidosA3Grupo
                  pedidos={pedidos}
                  puedeEditarPedidosA3={puedeEditarPedidosA3}
                  enviosCountByPedido={enviosCountByPedido}
                  selectedPedidoId={selectedPedidoId}
                  onEditRow={handleOpenEditPedido}
                  onDeleteRow={handleDeleteA3Pedido}
                  onEnviosRow={(row) => setEnviosDialogCtx(row)}
                />
              </AccordionDetails>
            </Accordion>
          );
        })}
          </Box>
        </Box>
      )}
      </Box>
      <Dialog
        open={cpBackfillDialogOpen}
        onClose={() =>
          cpBackfillLoading ? null : setCpBackfillDialogOpen(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("produccionSummary.cpDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("produccionSummary.cpDialogBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCpBackfillDialogOpen(false)}
            disabled={cpBackfillLoading}
          >
            {t("produccionSummary.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRunCpBackfill}
            disabled={cpBackfillLoading}
          >
            {cpBackfillLoading
              ? t("produccionSummary.executing")
              : t("produccionSummary.confirmReconcile")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteA3DialogOpen}
        onClose={closeDeleteA3Dialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("produccionSummary.deleteDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("produccionSummary.deleteDialogBody")}
          </DialogContentText>
          {deleteA3DialogError ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {deleteA3DialogError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDeleteA3Dialog}
            disabled={deleteA3DialogLoading}
          >
            {t("produccionSummary.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteA3Pedido}
            disabled={deleteA3DialogLoading}
          >
            {deleteA3DialogLoading
              ? t("produccionSummary.deleting")
              : t("produccionSummary.delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={unitsLimitDialogOpen}
        onClose={() => {
          setUnitsLimitDialogOpen(false);
          setPendingOverUnitsCreate(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("produccionSummary.unitsDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {unitsLimitDialogMessage ||
              t("produccionSummary.unitsDialogDefault")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUnitsLimitDialogOpen(false);
              setPendingOverUnitsCreate(null);
            }}
            variant="outlined"
            color="inherit"
          >
            {t("produccionSummary.cancel")}
          </Button>
          <Button
            onClick={async () => {
              try {
                if (pendingOverUnitsCreate) {
                  await runCreatePedido(pendingOverUnitsCreate);
                }
              } finally {
                setUnitsLimitDialogOpen(false);
                setPendingOverUnitsCreate(null);
              }
            }}
            variant="contained"
            color="warning"
          >
            {t("produccionSummary.continueAnyway")}
          </Button>
        </DialogActions>
      </Dialog>
      <ProduccionPedidosDialog
        open={pedidosDialogOpen}
        onClose={() => {
          setPedidosDialogOpen(false);
          setPedidoCrmEditando(null);
          setPedidoCrmCreandoBase(null);
        }}
        articulo={
          pedidoCrmEditando
            ? {
                CODART: pedidoCrmEditando.CodArt,
                CODPRO: pedidoCrmEditando.CodPro,
                CODFAMTALLAH:
                  pedidoCrmEditando.CodFamTallaH ??
                  pedidoCrmEditando.CODFAMTALLAH ??
                  "",
                CODFAMTALLAV:
                  pedidoCrmEditando.CodFamTallaV ??
                  pedidoCrmEditando.CODFAMTALLAV ??
                  "",
              }
            : pedidoCrmCreandoBase
              ? {
                  CODART: pedidoCrmCreandoBase.CodArt,
                  CODPRO: pedidoCrmCreandoBase.CodPro,
                  PRCCOMPRA: pedidoCrmCreandoBase.PrecioCosteUn,
                  PRCVENTA: pedidoCrmCreandoBase.PrecioVentaUn,
                  CODFAMTALLAH:
                    pedidoCrmCreandoBase.CodFamTallaH ??
                    pedidoCrmCreandoBase.CODFAMTALLAH ??
                    "",
                  CODFAMTALLAV:
                    pedidoCrmCreandoBase.CodFamTallaV ??
                    pedidoCrmCreandoBase.CODFAMTALLAV ??
                    "",
                }
              : null
        }
        presupuesto={
          pedidoCrmEditando
            ? {
                IdPresupuesto: Number(pedidoCrmEditando.IdPresupuesto || 0),
                Units:
                  Number(pedidoCrmEditando?.UnitsEstimadas) ||
                  Number(pedidoCrmEditando?.Units || 0) ||
                  Number(
                    pedidoCrmEditando?.UnidadesVenta ??
                      pedidoCrmEditando?.UnidadesCompra ??
                      1,
                  ) ||
                  1,
              }
            : pedidoCrmCreandoBase
              ? {
                  IdPresupuesto: Number(
                    pedidoCrmCreandoBase.IdPresupuesto || 0,
                  ),
                  Units:
                    Number(pedidoCrmCreandoBase?.UnitsEstimadas) ||
                    Number(pedidoCrmCreandoBase?.Units || 0) ||
                    Number(
                      pedidoCrmCreandoBase.UnidadesVenta ??
                        pedidoCrmCreandoBase.UnidadesCompra ??
                        1,
                    ) ||
                    1,
                }
              : null
        }
        clienteNombre={
          pedidoCrmEditando
            ? String(pedidoCrmEditando.ClienteNombre || "").trim()
            : String(pedidoCrmCreandoBase?.ClienteNombre || "").trim()
        }
        registroCrm={pedidoCrmEditando}
        pedidosContextRows={pedidosContextRowsForDialog}
        referenciaSuffix={referenciaPedidoSuffix}
        initialDbSource={
          String(
            pedidoCrmEditando?.EmpresaA3 ||
              pedidoCrmCreandoBase?.EmpresaA3 ||
              "STAGE_DESIGN",
          ).trim() || "STAGE_DESIGN"
        }
        lockDbSource={!pedidoCrmEditando}
        onConfirm={async (_pedidoPayload, { isEdit } = {}) => {
          if (!puedeEditarPedidosA3) return;
          const codartPayload = String(
            _pedidoPayload?.articulo ||
              pedidoCrmEditando?.CodArt ||
              pedidoCrmCreandoBase?.CodArt ||
              "",
          ).trim();
          const unitsPresupuesto = Number(
            (isEdit
              ? pedidoCrmEditando?.UnitsEstimadas
              : pedidoCrmCreandoBase?.UnitsEstimadas) ??
              pedidoCrmEditando?.Units ??
              pedidoCrmCreandoBase?.Units ??
              pedidoCrmEditando?.UnidadesVenta ??
              pedidoCrmEditando?.UnidadesCompra ??
              pedidoCrmCreandoBase?.UnidadesVenta ??
              pedidoCrmCreandoBase?.UnidadesCompra ??
              0,
          );
          const idPresupuestoObjetivo = Number(
            (isEdit
              ? pedidoCrmEditando?.IdPresupuesto
              : pedidoCrmCreandoBase?.IdPresupuesto) || 0,
          );
          const idProyectoObjetivo = Number(
            (isEdit
              ? pedidoCrmEditando?.IdProyecto
              : pedidoCrmCreandoBase?.IdProyecto) || 0,
          );
          const tipoContableObjetivo = normTipoContable(
            _pedidoPayload?.tipoContable ||
              pedidoCrmEditando?.TipoContableVenta ||
              pedidoCrmEditando?.TipoContableCompra ||
              "1",
          );
          if (!isEdit && codartPayload && unitsPresupuesto > 0) {
            const unidadesExistentes = rows.reduce((acc, row) => {
              if (
                String(row?.CodArt || "")
                  .trim()
                  .toUpperCase() !== codartPayload.toUpperCase()
              ) {
                return acc;
              }
              if (idPresupuestoObjetivo > 0) {
                const rowIdPresupuesto = Number(row?.IdPresupuesto || 0);
                if (rowIdPresupuesto !== idPresupuestoObjetivo) {
                  return acc;
                }
              }
              if (idProyectoObjetivo > 0) {
                const rowIdProyecto = Number(row?.IdProyecto || 0);
                if (rowIdProyecto !== idProyectoObjetivo) {
                  return acc;
                }
              }
              if (getRowTipoContable(row) !== tipoContableObjetivo) {
                return acc;
              }
              const uc = Number(row?.UnidadesCompra || 0);
              const uv = Number(row?.UnidadesVenta || 0);
              return acc + Math.max(uc, uv, 0);
            }, 0);
            const incomingCompra = parseNum(_pedidoPayload?.unidadesCompra);
            const incomingVenta = parseNum(_pedidoPayload?.unidadesVenta);
            const incomingUnits = (() => {
              if (isEdit) {
                const tienePedc = Boolean(pedidoCrmEditando?.IdPedc);
                const tienePedv = Boolean(pedidoCrmEditando?.IdPedv);
                if (tienePedc && !tienePedv) return Math.max(incomingCompra, 0);
                if (!tienePedc && tienePedv) return Math.max(incomingVenta, 0);
                return Math.max(incomingCompra, incomingVenta, 0);
              }
              const modoAlta = String(_pedidoPayload?.modo || "ambos")
                .trim()
                .toLowerCase();
              if (modoAlta === "compra") return Math.max(incomingCompra, 0);
              if (modoAlta === "venta") return Math.max(incomingVenta, 0);
              return Math.max(incomingCompra, incomingVenta, 0);
            })();
            const currentUnitsEditing = (() => {
              if (!isEdit) return 0;
              const tienePedc = Boolean(pedidoCrmEditando?.IdPedc);
              const tienePedv = Boolean(pedidoCrmEditando?.IdPedv);
              const uc = Number(pedidoCrmEditando?.UnidadesCompra || 0);
              const uv = Number(pedidoCrmEditando?.UnidadesVenta || 0);
              if (tienePedc && !tienePedv) return Math.max(uc, 0);
              if (!tienePedc && tienePedv) return Math.max(uv, 0);
              return Math.max(uc, uv, 0);
            })();
            const totalTrasGuardar = isEdit
              ? Math.max(0, unidadesExistentes - currentUnitsEditing) +
                incomingUnits
              : unidadesExistentes + incomingUnits;
            if (totalTrasGuardar - unitsPresupuesto > 0.0001) {
              setUnitsLimitDialogMessage(
                t("produccionSummary.unitsLimitMessage", {
                  codart: codartPayload,
                  tipo: tipoContableObjetivo,
                  total: totalTrasGuardar,
                  units: unitsPresupuesto,
                }),
              );
              setPendingOverUnitsCreate(_pedidoPayload);
              setUnitsLimitDialogOpen(true);
              return;
            }
          }
          if (!isEdit) {
            await runCreatePedido(_pedidoPayload);
            return;
          }
          if (!pedidoCrmEditando) return;
          const tienePedc = Boolean(pedidoCrmEditando?.IdPedc);
          const tienePedv = Boolean(pedidoCrmEditando?.IdPedv);
          const payloadUpd = {
            idPresupuestoA3Pedido: pedidoCrmEditando.IdPresupuestoA3Pedido,
            idPresupuesto: Number(pedidoCrmEditando.IdPresupuesto || 0),
            idProyecto: Number(pedidoCrmEditando.IdProyecto || 0),
            idPedc: pedidoCrmEditando.IdPedc,
            idPedv: pedidoCrmEditando.IdPedv,
            idLinCompra: pedidoCrmEditando.IdLinCompra,
            idLinVenta: pedidoCrmEditando.IdLinVenta,
            codart: String(_pedidoPayload?.articulo || "").trim(),
            codpro: String(_pedidoPayload?.proveedor || "").trim(),
            codcli: String(_pedidoPayload?.cliente || "").trim(),
            clienteNombre: String(_pedidoPayload?.clienteNombre || "").trim(),
            proveedorNombre: String(
              _pedidoPayload?.proveedorNombre || "",
            ).trim(),
            referencia: _pedidoPayload?.referencia,
            unidadesCompra: tienePedc
              ? parseNum(_pedidoPayload?.unidadesCompra)
              : 0,
            precioCoste: tienePedc ? parseNum(_pedidoPayload?.precioCoste) : 0,
            codMonCompra: String(_pedidoPayload?.monedaCompra || "").trim(),
            unidadesVenta: tienePedv
              ? parseNum(_pedidoPayload?.unidadesVenta)
              : 0,
            precioVenta: tienePedv ? parseNum(_pedidoPayload?.precioVenta) : 0,
            codMonVenta: String(_pedidoPayload?.monedaVenta || "").trim(),
            fecha: normalizeFechaIso(_pedidoPayload?.fecha),
            fechaCompra: normalizeFechaIso(_pedidoPayload?.fechaCompra),
            fechaVenta: normalizeFechaIso(_pedidoPayload?.fechaVenta),
            lineasTallas: Array.isArray(_pedidoPayload?.lineasTallas)
              ? _pedidoPayload.lineasTallas
              : [],
            centroCosteEmpresa: String(
              _pedidoPayload?.centroCosteEmpresa || "",
            ).trim(),
            centroCosteMarca: String(
              _pedidoPayload?.centroCosteMarca || "",
            ).trim(),
            centroCosteDepartamento: String(
              _pedidoPayload?.centroCosteDepartamento || "",
            ).trim(),
            dbSource: String(
              _pedidoPayload?.dbSource ||
                pedidoCrmEditando?.EmpresaA3 ||
                "STAGE_DESIGN",
            ).trim(),
          };
          await updateA3Pedidos(payloadUpd);
          await load();
          setPedidosDialogOpen(false);
          setPedidoCrmEditando(null);
          setPedidoCrmCreandoBase(null);
        }}
      />
      <Dialog
        open={postPedidoCpDialog.open}
        onClose={() => setPostPedidoCpDialog((p) => ({ ...p, open: false }))}
      >
        <DialogTitle>{t("produccionSummary.linkCpTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("produccionSummary.linkCpBody", {
              count: fmtA3Cantidad(postPedidoCpDialog.unidadesPrendas),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setPostPedidoCpDialog((p) => ({ ...p, open: false }))
            }
          >
            {t("produccionSummary.no")}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await linkCriticalPathFromPedido({
                  productId: postPedidoCpDialog.productId,
                  idPresupuestoA3Pedido:
                    postPedidoCpDialog.idPresupuestoA3Pedido,
                  unidadesPrendasPedido: postPedidoCpDialog.unidadesPrendas,
                });
              } catch (e) {
                console.error(e);
              } finally {
                setPostPedidoCpDialog((p) => ({ ...p, open: false }));
              }
            }}
          >
            {t("produccionSummary.yesAdd")}
          </Button>
        </DialogActions>
      </Dialog>
      <PedidoEnviosDialog
        open={Boolean(enviosDialogCtx)}
        onClose={() => setEnviosDialogCtx(null)}
        productId={Number(enviosDialogCtx?.IdProducto) || null}
        pedidoRow={enviosDialogCtx}
        presupuesto={{
          IdPresupuesto: Number(enviosDialogCtx?.IdPresupuesto),
          ProductId: Number(enviosDialogCtx?.IdProducto),
          Units:
            Number(enviosDialogCtx?.Units) ||
            Number(enviosDialogCtx?.UnidadesVenta) ||
            Number(enviosDialogCtx?.UnidadesCompra) ||
            undefined,
        }}
        onChanged={load}
      />
    </Box>
  );
};

export default ProduccionSummary;
